/**
 * Mobile Detection Utilities
 * Helps detect mobile devices and optimize wallet connection flow
 */

/**
 * Detects if the user is on a mobile device
 * @returns {boolean} True if mobile device detected
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * Detects if the user is on iOS
 * @returns {boolean} True if iOS device detected
 */
export const isIOS = () => {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

/**
 * Detects if the user is on Android
 * @returns {boolean} True if Android device detected
 */
export const isAndroid = () => {
  return /Android/i.test(navigator.userAgent);
};

/**
 * Checks if a wallet is injected (e.g., MetaMask browser extension or mobile app)
 * @returns {boolean} True if injected wallet detected
 */
export const hasInjectedWallet = () => {
  return typeof window.ethereum !== "undefined";
};

/**
 * Detects if user is in a mobile wallet's in-app browser
 * @returns {boolean} True if in wallet browser
 */
export const isInWalletBrowser = () => {
  const ua = navigator.userAgent.toLowerCase();
  return (
    ua.includes("metamask") ||
    ua.includes("trust") ||
    ua.includes("rainbow") ||
    ua.includes("coinbase")
  );
};

/**
 * Gets deep link URL for specific mobile wallets
 * @param {string} walletName - Name of the wallet (metamask, trust, rainbow)
 * @param {string} uri - WalletConnect URI
 * @returns {string} Deep link URL
 */
export const getMobileWalletDeepLink = (walletName, uri) => {
  const currentUrl = window.location.href;
  const host = window.location.host;

  const deepLinks = {
    metamask: `https://metamask.app.link/dapp/${host}`,
    trust: `trust://open_url?coin_id=60&url=${encodeURIComponent(currentUrl)}`,
    rainbow: uri ? `rainbow://wc?uri=${encodeURIComponent(uri)}` : `rainbow://`,
    coinbase: `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(
      currentUrl
    )}`,
  };

  return deepLinks[walletName.toLowerCase()] || currentUrl;
};

/**
 * Attempts to open a mobile wallet app
 * @param {string} walletName - Name of the wallet to open
 * @param {string} uri - Optional WalletConnect URI
 */
export const openMobileWallet = (walletName, uri = null) => {
  if (!isMobileDevice()) {
    console.warn("Not a mobile device, skipping deep link");
    return;
  }

  const deepLink = getMobileWalletDeepLink(walletName, uri);

  // Try to open the deep link
  window.location.href = deepLink;

  // Fallback: If app doesn't open in 2 seconds, could show app store link
  setTimeout(() => {
    if (document.hidden) {
      // App opened successfully
      return;
    }
    // Could show "Install wallet" message here
  }, 2000);
};

/**
 * Gets viewport dimensions (useful for modal sizing)
 * @returns {Object} Viewport width and height
 */
export const getViewportDimensions = () => {
  return {
    width: Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    ),
    height: Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0
    ),
  };
};

/**
 * Checks if device is in landscape mode
 * @returns {boolean} True if landscape
 */
export const isLandscape = () => {
  const { width, height } = getViewportDimensions();
  return width > height;
};

/**
 * Prevents body scroll (useful when modal is open)
 */
export const preventBodyScroll = () => {
  document.body.style.overflow = "hidden";
  document.body.style.position = "fixed";
  document.body.style.width = "100%";
};

/**
 * Restores body scroll
 */
export const restoreBodyScroll = () => {
  document.body.style.overflow = "";
  document.body.style.position = "";
  document.body.style.width = "";
};

/**
 * Triggers haptic feedback on supported devices
 * @param {string} type - Type of haptic feedback ('light', 'medium', 'heavy')
 */
export const triggerHapticFeedback = (type = "light") => {
  if (!navigator.vibrate) return;

  const patterns = {
    light: 10,
    medium: 20,
    heavy: 50,
  };

  navigator.vibrate(patterns[type] || patterns.light);
};
